/**
 * Type definitions for DocTypeListPageShell
 *
 * Extracted from DocTypeListPageShell.tsx for maintainability.
 */

import type { ColumnDef, SortingState, ExpandedState } from "@tanstack/react-table";
import type { useReactTable } from "@tanstack/react-table";
import type { FilterDescriptor } from "@/lib/erp";
import type { FrappeListResponse } from "@/lib/frappe-client";
import type { ListSettingsConfig, ListSettingsFieldConfig } from "@/lib/hooks/listSettingsTypes";

// Stable empty array references to prevent infinite loops in selectors / useMemo deps
export const EMPTY_FILTERS: FilterDescriptor[] = [];
export const EMPTY_SORTING: SortingState = [];

/**
 * Result type for list hooks
 * Ensures consistency with FrappeListResponse<T>
 */
export type ListHookResult<T> = {
  data?: FrappeListResponse<T>;
  isLoading: boolean;
  error: unknown;
  refetch?: () => void;
};

export type DocTypeListPageShellProps<T> = {
  doctype: string;
  columns:
    | ColumnDef<T, unknown>[]
    | ((ctx: {
        items: T[];
        visibleFieldOrder?: string[];
        availableFields?: ListSettingsFieldConfig[];
      }) => ColumnDef<T, unknown>[]);
  filterStoreKey: string;           // used to scope filter state in Zustand
  useListHook: (params: {
    filterDescriptors: FilterDescriptor[];
    searchQuery: string;
    pageIndex: number;
    pageSize: number;
    orderBy?: string;
    fields?: string[]; // Optional: fields to fetch from backend (defaults to hook's default)
  }) => ListHookResult<T>;
  defaultSorting?: SortingState;    // e.g. [{ id: "modified", desc: true }]
  pageSizeOptions?: number[];       // default: [20, 50, 100, 200, 400, 500]
  searchPlaceholder?: string;

  // Customizable parts
  title?: string;
  description?: string;
  newButtonLabel?: string;
  onNewClick?: () => void;
  onRowClick?: (row: T) => void;
  getRowId?: (row: T) => string;

  // Optional: Inline sub-row (e.g., detail panel) rendered below matched row
  /** Render an inline sub-row below the given row. Return null to hide. */
  renderSubRow?: (row: T, columns: import("./ListTableBody").SubRowColumnInfo) => React.ReactNode | null;
  /** ID of the row whose sub-row is currently active (for highlight styling) */
  activeSubRowId?: string;

  // Optional: Row hierarchy support (for variants, nested data, etc.)
  getSubRows?: (row: T) => T[] | undefined;
  defaultExpanded?: (row: T) => boolean; // Function to determine if a row should be expanded by default
  // Optional: Controlled expanded state (if provided, overrides internal state)
  expanded?: ExpandedState;
  onExpandedChange?: (updater: ExpandedState | ((prev: ExpandedState) => ExpandedState)) => void;
  // Optional: Column(s) to prepend to the left (e.g., expand/collapse)
  prependColumn?: ColumnDef<T, unknown>;
  prependColumns?: ColumnDef<T, unknown>[];

  // Optional: Left-side thumbnail column toggle (controlled via List Settings)
  thumbnailConfig?: {
    /**
     * Fieldname stored in List Settings to represent the toggle.
     * Must be provided via listSettingsConfig.staticFields to appear in the modal.
     */
    toggleFieldname?: string; // default "__thumbnail"
    /**
     * Candidate fieldnames in the row data that contain a file path/URL.
     * If omitted, we auto-detect the first Attach Image / Image field from meta.
     */
    imageFieldnames?: string[];
    /**
     * Candidate fieldnames for the fallback initial (e.g., item_name, customer_name).
     */
    labelFieldnames?: string[];
    /**
     * Thumbnail size (square).
     */
    sizePx?: number; // default 48
  };

  // Translations: match the prevailing local "TranslationModule" convention used by table components.
  // (Most components define `type TranslationModule = Record<string, string>`.)
  translations?: Record<string, string>;

  // Optional: Custom header component
  header?: React.ReactNode;

  // Optional: Content to render below the header (e.g., quick filters bar)
  // Use this instead of `header` when you want to keep the default header but add content below it.
  headerBelow?: React.ReactNode;
  // Optional: Header actions that receive the table instance and list context
  headerActions?: (
    table: ReturnType<typeof useReactTable<T>>,
    context?: {
      listSettings?: {
        availableFields: ListSettingsFieldConfig[];
        visibleFieldOrder: string[];
      };
      filterStoreKey?: string;
    },
  ) => React.ReactNode;

  // Optional: Custom styling
  className?: string;
  /** Remove max-width constraint — page stretches to fill available width */
  fullWidth?: boolean;
  /** Override the table container wrapper class (default: rounded-md card with border) */
  tableContainerClassName?: string;
  /** Remove all padding from the page container — edge-to-edge layout */
  noPadding?: boolean;
  /** ClickUp-style compact toolbar — smaller title, tighter spacing, compact buttons */
  compactMode?: boolean;
  /** Breadcrumb segments shown above compact toolbar (ClickUp-style) */
  breadcrumbs?: { label: string; href?: string; icon?: React.ReactNode }[];

  // Optional: List settings configuration (for dynamic column visibility/ordering)
  // Supports both static (backwards compatible) and meta-driven modes
  listSettingsConfig?: ListSettingsConfig;

  // Optional: System columns configuration (non-configurable right-side strip)
  systemColumnsConfig?: {
    enabled?: boolean;
    showAssignedTo?: boolean;
    showCreated?: boolean;
    showComments?: boolean;
    showLikes?: boolean;
    assignedToField?: string;      // default: "_assign"
    commentCountField?: string;    // default: "_comment_count"
    likedByField?: string;         // default: "_liked_by"
    createdField?: string;         // default: "creation"
  };

  // Optional: System column toggles for ListSettingsModal (passed from page-level)
  systemColumnToggles?: import("./ListSettingsModal").SystemColumnToggle[];
  onSystemColumnToggle?: (id: string, visible: boolean) => void;

  /** Callback when a search autocomplete result is explicitly selected (clicked or Enter).
   *  Use to open a detail panel or navigate — distinct from onChange which fires on every keystroke. */
  onSearchSelect?: (value: string) => void;

  // Optional: Search autocomplete configuration
  searchConfig?: {
    /** Whether to show initial suggestions when popover opens (default: true).
     *  Disable for high-volume doctypes like Stock Entry, Journal Entry */
    showInitialSuggestions?: boolean;
    /** Minimum characters before searching (default: 2).
     *  Increase for high-volume doctypes */
    minSearchLength?: number;
    /** Number of results to fetch (default: 20) */
    pageLength?: number;
    /** Optional filters to pass to search_link (Frappe format) */
    filters?: Record<string, unknown>;
  };

  /** Enable view mode switching (table ↔ gallery). Default: false */
  enableViewModeSwitch?: boolean;

  /** Enable simple view mode (Shopify-style list). Default: false.
   *  Requires simpleViewContent to render the actual content. */
  enableSimpleView?: boolean;

  /** Render function for simple view mode. Receives the shell's items/loading state.
   *  Only used when enableSimpleView is true. */
  renderSimpleView?: (context: { items: T[]; isLoading: boolean; total: number; refetch: () => void }) => React.ReactNode;

  /** Enable report view mode (table ↔ gallery ↔ report). Default: false */
  enableReportView?: boolean;

  /** Enable advanced table view mode. Default: false */
  enableAdvancedView?: boolean;

  /** Schema column configs for advanced table view (from ListViewSchema) */
  advancedColumns?: import('@/lib/views/listViewSchemas/types').ListViewColumnConfig[];

  /** i18n labels for advanced table components */
  advancedTableLabels?: Partial<import('@/components/advanced-table/advancedTableLabels').AdvancedTableLabels>;

  /** Custom cell renderers for advanced table columns (keyed by fieldname) */
  advancedColumnOverrides?: Record<string, (props: { row: import('@tanstack/react-table').Row<T> }) => React.ReactNode>;

  /** Pre-fetched select options for advanced filter dropdowns (keyed by fieldname) */
  advancedFilterOptions?: Record<string, { label: string; value: string }[]>;

  /** Set of fieldnames editable in data grid mode (advanced table) */
  advancedEditableFields?: Set<string>;

  /** i18n labels for data grid components */
  advancedDataGridLabels?: Partial<import('@/components/data-grid/dataGridLabels').DataGridLabels>;

  /** Enable inline editing for standard table cells. Auto-detects editable fields from DocType meta. */
  inlineEditable?: boolean;
  /** Whether inline edit mode is currently active (controlled by parent) */
  isInlineEditMode?: boolean;

  /** Whether "Show archived" toggle is active — controls Archive vs Restore in bulk action bars */
  showArchived?: boolean;

  /** Gallery view configuration */
  galleryConfig?: {
    /** Card size in pixels. Default: 160 */
    sizePx?: number;
    /** Fields to use for card title. Falls back to thumbnailConfig.labelFieldnames */
    titleFieldnames?: string[];
    /** Fields to use for subtitle */
    subtitleFieldnames?: string[];
  };
};

/**
 * Normalized system columns config (with defaults applied)
 */
export type NormalizedSystemConfig = {
  enabled: true;
  showAssignedTo: boolean;
  showCreated: boolean;
  showComments: boolean;
  showLikes: boolean;
  assignedToField: string;
  commentCountField: string;
  likedByField: string;
  createdField: string;
};
